import { checkOrganizationAndEnterpriseOwner, createModelNode, createNewDocumentData, createPartFromDocument, createPartVersionFromDocument, FootPrintShapeType, GENESIS_TERMINAL_PART_UID, headVersionName, makeDocumentPubliclyCommentable, slugifyDocumentName, } from "@buildwithflux/models";
import { guid } from "@buildwithflux/shared";
import { values } from "lodash";
import { KiCadFootprintImporter } from "../data_portability/importers/kicad/KiCad/v4/footprint/KiCadFootprintImporter";
import { KicadSymbolParser } from "../data_portability/importers/kicad/KiCad/v4/symbol/KicadSymbolParser";
import { buildFootprintAsset, buildModelAsset, buildSymbolAsset } from "../document_assets";
import { addPcbNodes, getFirstTopLevelFootprint, getFirstTopLevelModel, setElementsAndGenerateNodes } from "../nodes";
import { buildProperties, buildTerminals, drawInstructionsToSvg, extractOptionalMetaData, getRandomNumber, } from "./common/PartImportHelpers";
/* Responsible for building document assets from blobs and persisting them to object storage */
export class AssetCreationAPI {
    fileStorage;
    constructor(fileStorage) {
        this.fileStorage = fileStorage;
    }
    async createAssets(symbolAssetBlobs, footprintAssetBlob, modelAssetBlob) {
        const requests = [];
        if (!symbolAssetBlobs.length)
            throw new Error("One or more symbol assets is required.");
        // Symbol
        const schematicAssets = [];
        for (const symbolAssetBlob of symbolAssetBlobs) {
            const symbolAsset = buildSymbolAsset(symbolAssetBlob);
            requests.push(this.fileStorage.uploadFile(symbolAsset.storageName, Buffer.from(symbolAssetBlob.content), "image/svg+xml", true));
            schematicAssets.push(symbolAsset);
        }
        const assets = {
            schematicAssets,
            footprint: undefined,
            modelAsset: undefined,
        };
        // Footprint
        if (footprintAssetBlob) {
            const footprintAssetData = buildFootprintAsset(footprintAssetBlob);
            assets.footprint = {
                asset: footprintAssetData,
                content: Buffer.from(footprintAssetBlob.content).toString("base64"),
            };
            requests.push(this.fileStorage.uploadFile(footprintAssetData.storageName, Buffer.from(footprintAssetBlob.content), "application/octet-stream", true));
        }
        // Model
        if (modelAssetBlob) {
            const modelAssetData = buildModelAsset(modelAssetBlob);
            assets.modelAsset = modelAssetData;
            requests.push(this.fileStorage.uploadFile(modelAssetData.storageName, Buffer.from(modelAssetBlob.content), "application/octet-stream", true));
        }
        // TODO: Error logging when uploads fail
        await Promise.all(requests);
        return assets;
    }
}
/**
 * Responsible for persisting the document and part versions to the relevant repositories
 *
 * Can't be used from the frontend
 */
export class DocumentPersistenceAPI {
    documentRepository;
    partRepository;
    partVersionRepository;
    constructor(documentRepository, partRepository, partVersionRepository) {
        this.documentRepository = documentRepository;
        this.partRepository = partRepository;
        this.partVersionRepository = partVersionRepository;
    }
    async persistDocument(creator, partDocumentData, organization, enterprise) {
        await this.ensureUniqueDocumentName(creator, partDocumentData);
        checkOrganizationAndEnterpriseOwner(organization, enterprise);
        // Required for the document to be findable in the search by non-owners
        // See backend/functions/src/algolia/sync/helpers/partVersion.ts "has_custom_name"
        partDocumentData.has_custom_name = true;
        const newVersion = guid();
        const newPart = createPartFromDocument(partDocumentData, newVersion);
        const newPartVersion = createPartVersionFromDocument(partDocumentData, newPart.uid, newVersion);
        const thumbnailAsset = Object.values(partDocumentData.assets).find((asset) => asset.isThumbnail);
        if (thumbnailAsset) {
            newPartVersion.preview_image = thumbnailAsset.storageName;
            newPartVersion.symbol_resource_file = thumbnailAsset.storageName;
            partDocumentData.preview_image = thumbnailAsset.storageName;
        }
        partDocumentData.belongs_to_part_uid = newPart.uid;
        if (newPart.uid === GENESIS_TERMINAL_PART_UID || newPartVersion.part_uid === GENESIS_TERMINAL_PART_UID) {
            throw new Error("Cannot overwrite the genesis terminal part");
        }
        // NOTE: save the doc first, then the part, because cloud functions
        // might assume that they exist before the part version does. See
        // addPartToLibrary function.
        await this.documentRepository.save(partDocumentData);
        await this.partRepository.save(newPart);
        await this.partVersionRepository.save(newPartVersion);
        return true;
    }
    /**
     * @deprecated Does not ensure uniqueness for local components, nor enterprise projects - use the DocumentRenameService
     */
    async ensureUniqueDocumentName(creator, partDocumentData) {
        let documentNameExists = !!(await this.documentRepository.getBaseDataByPath(partDocumentData.slug, creator.handle, true));
        // Keep adding a random number to the end of the document name until it is unique
        while (documentNameExists) {
            partDocumentData.name = partDocumentData.name + " - " + getRandomNumber();
            partDocumentData.slug = slugifyDocumentName(partDocumentData.name);
            documentNameExists = !!(await this.documentRepository.getBaseDataByPath(partDocumentData.slug, creator.handle, true));
        }
    }
}
export class ImportDocumentService {
    config;
    partAssetStorage;
    documentRepository;
    partVersionRepository;
    assetCreationAPI;
    documentPersistenceAPI;
    constructor(config, partAssetStorage, documentRepository, partRepository, partVersionRepository) {
        this.config = config;
        this.partAssetStorage = partAssetStorage;
        this.documentRepository = documentRepository;
        this.partVersionRepository = partVersionRepository;
        this.assetCreationAPI = new AssetCreationAPI(this.partAssetStorage);
        this.documentRepository = documentRepository;
        this.documentPersistenceAPI = new DocumentPersistenceAPI(documentRepository, partRepository, partVersionRepository);
    }
    async buildDocument(name, creator, assets, tags = [], skipIfExists = true, organization, enterprise) {
        const documentSlug = slugifyDocumentName(name);
        if (skipIfExists) {
            if (enterprise &&
                organization &&
                (await this.documentRepository.getBaseDataByEnterpriseOwnerHandlesAndSlug(enterprise.handle, organization.handle, documentSlug))) {
                return false;
            }
            else if (organization &&
                (await this.documentRepository.getBaseDataByOwnerHandleAndSlug(organization.handle, documentSlug))) {
                return false;
            }
            else if (await this.documentRepository.getBaseDataByOwnerHandleAndSlug(creator.handle, documentSlug)) {
                return false;
            }
        }
        const parsedParts = KicadSymbolParser(assets.symbolAsset.content);
        const parsedPart = parsedParts && parsedParts[0];
        if (!parsedPart) {
            throw new Error("No part found in schematic");
        }
        const genesisTerminalPart = await this.getGenesisTerminalPart();
        const { symbols, ...importData } = parsedPart;
        const symbolAssets = [];
        for (const symbol of symbols) {
            const { symbolAsset, symbolOffset } = drawInstructionsToSvg(symbol.drawInstructions, symbol.name ? `${importData.name} ${symbol.name}` : importData.name);
            if (symbols.length > 1) {
                symbolAsset.associatedSymbol = symbol.name;
            }
            symbolAssets.push(symbolAsset);
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            symbol.terminalsData.forEach((terminal) => {
                terminal.symbolPosition.x += symbolOffset.x;
                terminal.symbolPosition.y += symbolOffset.y;
            });
        }
        await extractOptionalMetaData(assets, importData, this.config.scraperapi.api_key);
        const assetDescriptions = await this.assetCreationAPI.createAssets(symbolAssets, assets.footprint, assets.modelAsset);
        const newDocument = createNewDocumentData(creator, organization, enterprise);
        const hashtags = tags.map((tag) => "#" + tag).join(" ");
        // Basic Document Info
        newDocument.name = name;
        newDocument.slug = documentSlug;
        newDocument.description = [importData.description, hashtags].join(" ");
        newDocument.properties = buildProperties(importData);
        // Accessibility
        makeDocumentPubliclyCommentable(newDocument);
        setElementsAndGenerateNodes(newDocument, buildTerminals(symbols, genesisTerminalPart, creator));
        this.setAssetRules(newDocument, assetDescriptions);
        // Persist Document
        return await this.documentPersistenceAPI.persistDocument(creator, newDocument, organization, enterprise);
    }
    setAssetRules(newDocument, assetDescriptions) {
        // Symbol
        for (const symbolAsset of Object.values(assetDescriptions.schematicAssets)) {
            newDocument.assets[symbolAsset.uid] = symbolAsset;
        }
        // Footprint
        if (assetDescriptions.footprint) {
            newDocument.assets[assetDescriptions.footprint.asset.uid] = assetDescriptions.footprint.asset;
            const footprintNode = getFirstTopLevelFootprint(newDocument.pcbLayoutNodes);
            if (footprintNode) {
                footprintNode.pcbNodeRuleSet.asset = {
                    uid: guid(),
                    key: "asset",
                    payloadFileName: assetDescriptions.footprint.asset.storageName,
                    value: assetDescriptions.footprint.asset.name,
                    payloadAsBase64: assetDescriptions.footprint.content,
                    order: 1,
                };
            }
        }
        // Model
        if (assetDescriptions.modelAsset) {
            newDocument.assets[assetDescriptions.modelAsset.uid] = assetDescriptions.modelAsset;
            addPcbNodes(newDocument.pcbLayoutNodes, [createModelNode("ROOT", "Model")]);
            const modelNode = getFirstTopLevelModel(newDocument.pcbLayoutNodes);
            if (modelNode) {
                modelNode.pcbNodeRuleSet.asset = {
                    uid: guid(),
                    key: "asset",
                    payloadFileName: assetDescriptions.modelAsset.storageName,
                    value: assetDescriptions.modelAsset.name,
                    order: 1,
                };
                // set correct model scale and rotation
                if (assetDescriptions.footprint?.content) {
                    const decodedFootprintContent = atob(assetDescriptions.footprint.content);
                    const footprintParser = new KiCadFootprintImporter(decodedFootprintContent);
                    const parsedFootprint = values(footprintParser.parse())[0];
                    const kicadModels = parsedFootprint?.shapes.filter((shape) => shape.type === FootPrintShapeType.model);
                    if (kicadModels) {
                        const kicadModel = kicadModels[0];
                        if (kicadModel?.rotate &&
                            (kicadModel.rotate.x !== 0 || kicadModel.rotate.y !== 0 || kicadModel.rotate.z !== 0)) {
                            modelNode.pcbNodeRuleSet.rotation = {
                                uid: guid(),
                                key: "rotation",
                                value: `${-kicadModel.rotate.x} ${-kicadModel.rotate.y} ${-kicadModel.rotate.z}`,
                                order: 2,
                            };
                        }
                        if (kicadModel?.scale &&
                            (kicadModel.scale.x !== 1 || kicadModel.scale.y !== 1 || kicadModel.scale.z !== 1)) {
                            modelNode.pcbNodeRuleSet.scale = {
                                uid: guid(),
                                key: "scale",
                                value: `${kicadModel.scale.x} ${kicadModel.scale.y} ${kicadModel.scale.z}`,
                                order: 3,
                            };
                        }
                    }
                }
            }
        }
    }
    async getGenesisTerminalPart() {
        return this.partVersionRepository.get(GENESIS_TERMINAL_PART_UID, headVersionName);
    }
}
//# sourceMappingURL=service.js.map